'use client';

import { useCallback, useEffect, useState } from 'react';
import { CircleArrowLeft, CircleCheck } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop';
import { apiFetch } from '@/lib/api/apiClient';
import ApiError from '@/lib/api/apiError';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import { cropAndCompressImage } from '@/utils/helpers/imageCropHelper';
import ErrorBox, { ErrorMessage } from '@/components/ErrorBox';
import ImageAdaptive from '@/components/ImageAdaptive';
import Slider from '@/components/Slider';

export default function DashboardPersonalAvatar() {
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [completedCrop, setCompletedCrop] = useState<Area | null>(null);
    const [blobImage, setBlobImage] = useState<Blob | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
    const closeModal = useModalStore((state) => state.closeModal);
    const setUserAvatarUrl = useAuthStore((s) => s.setUserAvatarUrl);
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        let url: string | null = null;
        if (blobImage) {
            url = URL.createObjectURL(blobImage);
        }
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [blobImage]);

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'] },
        onDrop: (accepted) => {
            if (accepted.length > 0) setAvatarFile(accepted[0]);
        },
    });

    const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
        setCompletedCrop(croppedAreaPixels);
    }, []);

    const handleSubmit = async () => {
        setIsProcessing(true);
        try {
            if (!avatarFile || !completedCrop) {
                throw new Error('Crop未完了');
            }
            const { blob, ext } = await cropAndCompressImage(avatarFile, completedCrop);
            setBlobImage(blob);
            const d = new FormData();
            d.append('avatar_file', blob);
            d.append('avatar_extension', ext);
            const res = await apiFetch('/users/me/avatar', {
                method: 'POST',
                body: d,
            });
            closeModal();
            setUserAvatarUrl(res.avatar_resource_url);
            await showToastAsync('アバターの設定が完了しました', 'success');
        } catch (e: unknown) {
            if (e instanceof ApiError) {
                setErrorMessage({ message: e.message });
            }
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (!avatarFile) {
            setPreviewUrl(null);
            return;
        }
        const objectUrl = URL.createObjectURL(avatarFile);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [avatarFile]);

    return (
        <div className="font-corp font-medium flex flex-col justify-center items-center px-5 w-full h-full">
            {!avatarFile ? (
                <>
                    {/* Drop */}
                    <div className="flex justify-center items-center py-2 w-full h-full">
                        <div
                            {...getRootProps()}
                            className="flex flex-col w-full h-full md:h-auto md:aspect-video border-2 border-dashed items-center justify-center cursor-pointer"
                        >
                            <input {...getInputProps()} />
                            <div className="relative w-full flex items-center justify-center select-none">
                                <ImageAdaptive
                                    src="/images/modal/upload.svg"
                                    baseWidth={200}
                                    breakpoints={{ md: 300 }}
                                    className="absolute opacity-20"
                                />
                                <p className="text-center">『ドラッグ＆ドロップ』または『クリック』でファイルを選択</p>
                                <div className="absolute top-12 flex justify-center w-full">
                                    <div className="flex">
                                        <ImageAdaptive src="/images/file/extension-jpg.svg" baseWidth={40} />
                                    </div>
                                    <div className="px-4"></div>
                                    <div className="flex">
                                        <ImageAdaptive src="/images/file/extension-png.svg" baseWidth={40} />
                                    </div>
                                    <div className="px-4"></div>
                                    <div className="flex">
                                        <ImageAdaptive src="/images/file/extension-gif.svg" baseWidth={40} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {errorMessage && <ErrorBox message={errorMessage.message} />}
                </>
            ) : (
                <>
                    {/* Crop */}
                    <div className="w-full h-[380px] relative overflow-hidden">
                        {previewUrl && (
                            <Cropper
                                image={previewUrl}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </div>
                    <div className="flex w-full justify-center items-center pt-4">
                        <div className="flex justify-center items-center w-[50px] h-full">
                            <CircleArrowLeft
                                size={30}
                                strokeWidth={2}
                                onClick={() => setAvatarFile(null)}
                                className="text-color-co-gray-dark cursor-pointer"
                            />
                        </div>
                        <div className="flex items-center flex-grow px-4">
                            <Slider value={zoom} min={1} max={3} onChange={setZoom} />
                        </div>
                        <div className="flex justify-center items-center w-[50px] h-[50px]">
                            {isProcessing ? (
                                <ImageAdaptive
                                    src="/images/common/loading-progress.svg"
                                    baseWidth={40}
                                    breakpoints={{ md: 50 }}
                                />
                            ) : (
                                <CircleCheck
                                    size={30}
                                    strokeWidth={2}
                                    onClick={handleSubmit}
                                    className="text-color-form-button-check cursor-pointer"
                                />
                            )}
                        </div>
                    </div>
                    {errorMessage && <ErrorBox message={errorMessage.message} />}
                </>
            )}
        </div>
    );
}
